import React, { useState, useEffect } from "react";
import "./TaskDashboard.css";
import TaskCard from "../TaskCard/TaskCard";
import CreateTask from "../CreateTask/CreateTask";
import TaskStats from "../TaskStats/TaskStats";
import TaskFilters from "../TaskFilters/TaskFilters";
import MonthlyTaskReport from "./MonthlyTaskReport";
import Sidebar from "../Sidebar/Sidebar";
import DashboardLayout from "../Dashboard/DashboardLayout";
import { useAuth } from "../Login/AuthContext";
import { useNavigate } from "react-router-dom";

const TaskDashboard = () => {
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [updateModalType, setUpdateModalType] = useState("");
  const [updateDetails, setUpdateDetails] = useState([]);
  
  // Add Monthly Report State
  const [showMonthlyReport, setShowMonthlyReport] = useState(false);
  
  const [filters, setFilters] = useState({
    status: "all",
    priority: "all",
    sortBy: "deadline",
  });
  const [searchTerm, setSearchTerm] = useState("");

  // Attendance states
  const [attendanceData, setAttendanceData] = useState(null);
  const [attendanceLoading, setAttendanceLoading] = useState(false);

  // Get user role and info from localStorage
  const userRole = localStorage.getItem("userRole") || "employee";
  const userId = localStorage.getItem("userId");
  const userName = localStorage.getItem("userName") || "User";
  const canCreateTasks = ["admin", "hr", "supervisor"].includes(userRole);
  const isEmployee = userRole === "employee";

  const { logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchTasks();
    fetchStats();
    fetchTodayAttendance();
  }, []);

  useEffect(() => {
    filterTasks();
  }, [tasks, filters, searchTerm]);

  // Enhanced fetchTasks with role-based filtering
  const fetchTasks = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");

      if (!token) {
        logout();
        navigate("/login");
        return;
      }

      // Try main endpoint first, fallback to legacy
      let response = await fetch(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/tasks/my-tasks`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      // If main endpoint fails, try legacy
      if (!response.ok) {
        console.log("Main endpoint failed, trying legacy...");
        response = await fetch(
          `${process.env.REACT_APP_BACKEND_BASEURL}/api/tasks/my-tasks-legacy`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
      }

      if (!response.ok) {
        if (response.status === 401) {
          logout();
          navigate("/login");
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        let filteredTasks = data.tasks;

        // For employees, show only their assigned tasks
        if (isEmployee && userId) {
          const beforeFilter = data.tasks.length;
          filteredTasks = data.tasks.filter(
            (task) =>
              task.assignedTo &&
              (task.assignedTo._id === userId ||
                task.assignedTo.id === userId ||
                task.assignedTo === userId)
          );
          console.log(`Employee ${userName}:`);
          console.log(`- Total tasks from API: ${beforeFilter}`);
          console.log(`- Filtered tasks for user: ${filteredTasks.length}`);
          console.log(`- User ID: ${userId}`);
        }

        const enhancedTasks = filteredTasks.map((task) => {
          const latestDailyUpdate =
            task.dailyUpdates && task.dailyUpdates.length > 0
              ? task.dailyUpdates[task.dailyUpdates.length - 1]
              : null;

          const latestWeeklyUpdate =
            task.weeklyUpdates && task.weeklyUpdates.length > 0
              ? task.weeklyUpdates[task.weeklyUpdates.length - 1]
              : null;

          const totalTimeSpent = task.dailyUpdates
            ? Math.round(
                task.dailyUpdates.reduce(
                  (total, update) => total + (update.timeSpent || 0),
                  0
                ) / 60
              )
            : 0;

          const hasTodaysUpdate =
            task.dailyUpdates && task.dailyUpdates.length > 0
              ? (() => {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  return task.dailyUpdates.some((update) => {
                    const updateDate = new Date(update.date);
                    updateDate.setHours(0, 0, 0, 0);
                    return updateDate.getTime() === today.getTime();
                  });
                })()
              : false;

          return {
            ...task,
            latestDailyUpdate,
            latestWeeklyUpdate,
            updatesSummary: {
              totalDailyUpdates: task.dailyUpdates
                ? task.dailyUpdates.length
                : 0,
              totalWeeklyUpdates: task.weeklyUpdates
                ? task.weeklyUpdates.length
                : 0,
              totalTimeSpent,
              hasTodaysUpdate,
            },
          };
        });

        setTasks(enhancedTasks);
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem("authToken");
      let response = await fetch(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/tasks/stats`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      // Fallback to legacy stats
      if (!response.ok) {
        response = await fetch(
          `${process.env.REACT_APP_BACKEND_BASEURL}/api/tasks/stats-legacy`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
      }

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setStats(data.stats);
        }
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const fetchTodayAttendance = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/attendance/today`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setAttendanceData(data);
        }
      }
    } catch (error) {
      console.error("Error fetching attendance:", error);
    }
  };

  // Add Bulk Export Function
  const handleBulkTaskExport = async () => {
    try {
      const token = localStorage.getItem('authToken');
      
      // Create CSV content from current tasks
      let csvContent = "data:text/csv;charset=utf-8,";
      
      // Header
      csvContent += "Task Title,Assigned To,Status,Priority,Created Date,Deadline,Description,Time Spent (hrs)\n";
      
      // Data rows
      tasks.forEach(task => {
        const assignedTo = task.assignedTo?.name || 'Unassigned';
        const createdDate = new Date(task.createdAt).toLocaleDateString();
        const deadline = task.deadline ? new Date(task.deadline).toLocaleDateString() : 'No deadline';
        const description = task.description?.replace(/"/g, '""') || '';
        const timeSpent = task.updatesSummary?.totalTimeSpent || 0;
        
        csvContent += `"${task.title}","${assignedTo}","${task.status}","${task.priority}","${createdDate}","${deadline}","${description}",${timeSpent}\n`;
      });
      
      // Download
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `all_tasks_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } catch (error) {
      console.error('Error exporting tasks:', error);
      alert('Error exporting tasks');
    }
  };

  const handleQuickCheckIn = async () => {
    try {
      setAttendanceLoading(true);
      const token = localStorage.getItem("authToken");
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/attendance/check-in`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            location: "Remote",
            notes: "Quick check-in from dashboard",
          }),
        }
      );

      const data = await response.json();
      if (data.success) {
        alert("✅ Checked in successfully!");
        fetchTodayAttendance();
      } else {
        alert(data.message);
      }
    } catch (error) {
      console.error("Error checking in:", error);
      alert("Error checking in");
    } finally {
      setAttendanceLoading(false);
    }
  };

  const handleQuickCheckOut = async () => {
    try {
      setAttendanceLoading(true);
      const token = localStorage.getItem("authToken");
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/attendance/check-out`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            location: "Remote",
            notes: "Quick check-out from dashboard",
          }),
        }
      );

      const data = await response.json();
      if (data.success) {
        alert("✅ Checked out successfully!");
        fetchTodayAttendance();
      } else {
        alert(data.message);
      }
    } catch (error) {
      console.error("Error checking out:", error);
      alert("Error checking out");
    } finally {
      setAttendanceLoading(false);
    }
  };

  const filterTasks = () => {
    let filtered = [...tasks];

    if (filters.status !== "all") {
      filtered = filtered.filter((task) => task.status === filters.status);
    }

    if (filters.priority !== "all") {
      filtered = filtered.filter((task) => task.priority === filters.priority);
    }

    if (searchTerm) {
      filtered = filtered.filter(
        (task) =>
          task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          task.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    filtered.sort((a, b) => {
      switch (filters.sortBy) {
        case "deadline":
          return new Date(a.deadline) - new Date(b.deadline);
        case "priority":
          const priorityOrder = { urgent: 4, high: 3, medium: 2, low: 1 };
          return priorityOrder[b.priority] - priorityOrder[a.priority];
        case "status":
          return a.status.localeCompare(b.status);
        case "created":
          return new Date(b.createdAt) - new Date(a.createdAt);
        default:
          return 0;
      }
    });

    setFilteredTasks(filtered);
  };

  const handleUpdateStatusClick = (type) => {
    let details = [];

    if (type === "today") {
      details = tasks
        .filter((task) => task.updatesSummary?.hasTodaysUpdate)
        .map((task) => ({
          taskId: task._id,
          title: task.title,
          assignedTo: task.assignedTo?.name || "Unassigned",
          update: task.latestDailyUpdate,
          timeSpent: task.latestDailyUpdate
            ? task.latestDailyUpdate.timeSpent
            : 0,
          priority: task.priority,
          status: task.status,
        }));
    } else if (type === "all") {
      details = tasks
        .filter((task) => task.updatesSummary?.totalDailyUpdates > 0)
        .map((task) => ({
          taskId: task._id,
          title: task.title,
          assignedTo: task.assignedTo?.name || "Unassigned",
          totalUpdates: task.updatesSummary.totalDailyUpdates,
          totalTimeSpent: task.updatesSummary.totalTimeSpent,
          lastUpdate: task.latestDailyUpdate?.date,
          latestWork: task.latestDailyUpdate?.workDone,
          priority: task.priority,
          status: task.status,
        }));
    }

    setUpdateDetails(details);
    setUpdateModalType(type);
    setShowUpdateModal(true);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getTimeAgo = (date) => {
    const now = new Date();
    const updateDate = new Date(date);
    const diffTime = now - updateDate;
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));

    if (diffHours === 0) {
      return "Recently";
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else {
      const diffDays = Math.floor(diffHours / 24);
      return `${diffDays}d ago`;
    }
  };

  const handleTaskUpdate = (updatedTask) => {
    setTasks(
      tasks.map((task) => (task._id === updatedTask._id ? updatedTask : task))
    );
    fetchStats();
  };

  const handleTaskCreated = (newTask) => {
    setTasks([newTask, ...tasks]);
    setShowCreateTask(false);
    fetchStats();
  };

  const handleTaskDelete = (taskId) => {
    setTasks(tasks.filter((task) => task._id !== taskId));
    setFilteredTasks(filteredTasks.filter((task) => task._id !== taskId));
    fetchStats();
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // EMPLOYEE VIEW - NO SIDEBAR, NO DASHBOARD LAYOUT
  if (isEmployee) {
    return (
      <div
        style={{
          padding: "20px",
          minHeight: "100vh",
          backgroundColor: "#f5f5f5",
          fontFamily: "Arial, sans-serif",
        }}
      >
        {/* Employee Header with Logout */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
            padding: "20px",
            backgroundColor: "white",
            borderRadius: "8px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          <div>
            <h1 style={{ fontSize: "24px", margin: 0, color: "#333" }}>
              Welcome, {userName}
            </h1>
            <p style={{ margin: "5px 0 0 0", color: "#666" }}>
              Your Task Dashboard - {filteredTasks.length} tasks assigned
            </p>
          </div>
          <button
            onClick={handleLogout}
            style={{
              padding: "10px 20px",
              backgroundColor: "#dc3545",
              color: "white",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "500",
            }}
          >
            <i className="fas fa-sign-out-alt"></i> Logout
          </button>
        </div>

        {loading ? (
          <div
            style={{
              textAlign: "center",
              padding: "50px",
              backgroundColor: "white",
              borderRadius: "8px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
          >
            <i
              className="fas fa-spinner fa-spin"
              style={{ fontSize: "24px", color: "#007bff" }}
            ></i>
            <p style={{ marginTop: "10px", color: "#666" }}>
              Loading your tasks...
            </p>
          </div>
        ) : (
          <>
            <TaskStats stats={stats} />

            {/* Attendance Section */}
            <div
              style={{
                marginBottom: "20px",
                backgroundColor: "white",
                borderRadius: "8px",
                padding: "20px",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              <h2
                style={{
                  fontSize: "18px",
                  marginBottom: "15px",
                  color: "#333",
                }}
              >
                <i
                  className="fas fa-clock"
                  style={{ marginRight: "8px", color: "#007bff" }}
                ></i>
                Today's Attendance
              </h2>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                  gap: "15px",
                }}
              >
                {/* Check-in Card */}
                <div
                  style={{
                    border: "1px solid #e9ecef",
                    borderRadius: "8px",
                    padding: "15px",
                    backgroundColor: "#f8f9fa",
                  }}
                >
                  <h3
                    style={{
                      fontSize: "16px",
                      margin: "0 0 10px 0",
                      color: "#28a745",
                    }}
                  >
                    <i
                      className="fas fa-sign-in-alt"
                      style={{ marginRight: "8px" }}
                    ></i>
                    Check In
                  </h3>
                  {attendanceData?.attendance?.checkIn?.time ? (
                    <div style={{ color: "#28a745" }}>
                      <i
                        className="fas fa-check-circle"
                        style={{ marginRight: "5px" }}
                      ></i>
                      <span>
                        Checked in at{" "}
                        {new Date(
                          attendanceData.attendance.checkIn.time
                        ).toLocaleTimeString("en-IN", {
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                          timeZone: "Asia/Kolkata"
                        })}
                      </span>
                    </div>
                  ) : (
                    <div>
                      {attendanceData?.canCheckIn ? (
                        <button
                          onClick={handleQuickCheckIn}
                          disabled={attendanceLoading}
                          style={{
                            padding: "8px 16px",
                            backgroundColor: "#28a745",
                            color: "white",
                            border: "none",
                            borderRadius: "5px",
                            cursor: attendanceLoading
                              ? "not-allowed"
                              : "pointer",
                            fontSize: "14px",
                          }}
                        >
                          {attendanceLoading
                            ? "Checking In..."
                            : "Check In Now"}
                        </button>
                      ) : (
                        <div style={{ color: "#6c757d", fontSize: "14px" }}>
                          <i
                            className="fas fa-clock"
                            style={{ marginRight: "5px" }}
                          ></i>
                          <span>10:00-11:00 AM</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Check-out Card */}
                <div
                  style={{
                    border: "1px solid #e9ecef",
                    borderRadius: "8px",
                    padding: "15px",
                    backgroundColor: "#f8f9fa",
                  }}
                >
                  <h3
                    style={{
                      fontSize: "16px",
                      margin: "0 0 10px 0",
                      color: "#dc3545",
                    }}
                  >
                    <i
                      className="fas fa-sign-out-alt"
                      style={{ marginRight: "8px" }}
                    ></i>
                    Check Out
                  </h3>
                  {attendanceData?.attendance?.checkOut?.time ? (
                    <div style={{ color: "#28a745" }}>
                      <i
                        className="fas fa-check-circle"
                        style={{ marginRight: "5px" }}
                      ></i>
                      <span>
                        Checked out at{" "}
                        {new Date(
                          attendanceData.attendance.checkOut.time
                        ).toLocaleTimeString("en-IN", {
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                          timeZone: "Asia/Kolkata"
                        })}
                      </span>
                    </div>
                  ) : (
                    <div>
                      {attendanceData?.canCheckOut ? (
                        <button
                          onClick={handleQuickCheckOut}
                          disabled={attendanceLoading}
                          style={{
                            padding: "8px 16px",
                            backgroundColor: "#dc3545",
                            color: "white",
                            border: "none",
                            borderRadius: "5px",
                            cursor: attendanceLoading
                              ? "not-allowed"
                              : "pointer",
                            fontSize: "14px",
                          }}
                        >
                          {attendanceLoading
                            ? "Checking Out..."
                            : "Check Out Now"}
                        </button>
                      ) : (
                        <div style={{ color: "#6c757d", fontSize: "14px" }}>
                          <i
                            className="fas fa-clock"
                            style={{ marginRight: "5px" }}
                          ></i>
                          <span>After 5:00 PM</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Status Card */}
                <div
                  style={{
                    border: "1px solid #e9ecef",
                    borderRadius: "8px",
                    padding: "15px",
                    backgroundColor: "#f8f9fa",
                  }}
                >
                  <h3
                    style={{
                      fontSize: "16px",
                      margin: "0 0 10px 0",
                      color: "#007bff",
                    }}
                  >
                    <i
                      className="fas fa-calendar-day"
                      style={{ marginRight: "8px" }}
                    ></i>
                    Today's Status
                  </h3>
                  <span
                    style={{
                      padding: "4px 8px",
                      borderRadius: "4px",
                      fontSize: "12px",
                      fontWeight: "bold",
                      backgroundColor: attendanceData?.attendance?.status
                        ? "#28a745"
                        : "#6c757d",
                      color: "white",
                    }}
                  >
                    {attendanceData?.attendance?.status
                      ? attendanceData.attendance.status.toUpperCase()
                      : "NOT MARKED"}
                  </span>
                  {attendanceData?.attendance?.workingHours?.total > 0 && (
                    <div
                      style={{
                        marginTop: "8px",
                        fontSize: "14px",
                        color: "#666",
                      }}
                    >
                      <i
                        className="fas fa-hourglass-half"
                        style={{ marginRight: "5px" }}
                      ></i>
                      <span>
                        {Math.floor(
                          attendanceData.attendance.workingHours.total / 60
                        )}
                        h {attendanceData.attendance.workingHours.total % 60}m
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Task Controls */}
            <div
              style={{
                backgroundColor: "white",
                padding: "20px",
                borderRadius: "8px",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                marginBottom: "20px",
                display: "flex",
                gap: "15px",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <div
                style={{ flex: "1", minWidth: "200px", position: "relative" }}
              >
                <i
                  className="fas fa-search"
                  style={{
                    position: "absolute",
                    left: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#666",
                  }}
                ></i>
                <input
                  type="text"
                  placeholder="Search your tasks..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 10px 10px 35px",
                    border: "1px solid #ddd",
                    borderRadius: "5px",
                    fontSize: "14px",
                    outline: "none",
                  }}
                />
              </div>
              <TaskFilters filters={filters} onFilterChange={setFilters} />
            </div>

            {/* Tasks Grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))",
                gap: "20px",
              }}
            >
              {filteredTasks.length === 0 ? (
                <div
                  style={{
                    gridColumn: "1 / -1",
                    textAlign: "center",
                    padding: "60px 20px",
                    backgroundColor: "white",
                    borderRadius: "8px",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  }}
                >
                  <i
                    className="fas fa-tasks"
                    style={{
                      fontSize: "48px",
                      color: "#ccc",
                      marginBottom: "20px",
                    }}
                  ></i>
                  <h3
                    style={{
                      fontSize: "20px",
                      margin: "0 0 10px 0",
                      color: "#333",
                    }}
                  >
                    No tasks assigned
                  </h3>
                  <p style={{ color: "#666", margin: 0 }}>
                    {searchTerm
                      ? "No tasks match your search criteria"
                      : "No tasks have been assigned to you yet"}
                  </p>
                </div>
              ) : (
                filteredTasks.map((task) => (
                  <TaskCard
                    key={task._id}
                    task={task}
                    onUpdate={handleTaskUpdate}
                    onDelete={handleTaskDelete}
                    userRole={userRole}
                  />
                ))
              )}
            </div>
          </>
        )}
      </div>
    );
  }

  // ADMIN/HR/SUPERVISOR VIEW - WITH SIDEBAR AND ENHANCED FEATURES
  return (
    <DashboardLayout>
      <div className="task-dashboard">
        <Sidebar />
        
        {/* Enhanced Header with Report and Export Buttons */}
        <div className="task-dashboard-header">
          <div className="header-content">
            <h1>Task & Attendance Dashboard</h1>
            <p>Manage tasks and track your attendance</p>
          </div>
          <div className="header-actions">
            {/* Monthly Report Button */}
            {canCreateTasks && (
              <button
                className="report-btn"
                onClick={() => setShowMonthlyReport(true)}
                title="Generate Monthly Report"
              >
                <i className="fas fa-chart-bar"></i>
                Monthly Report
              </button>
            )}
            
            {/* Export All Tasks Button */}
            {canCreateTasks && (
              <button
                className="export-btn"
                onClick={handleBulkTaskExport}
                title="Export All Tasks"
              >
                <i className="fas fa-download"></i>
                Export Tasks
              </button>
            )}
            
            {/* Create Task Button */}
            {canCreateTasks && (
              <button
                className="create-task-btn"
                onClick={() => setShowCreateTask(true)}
              >
                <i className="fas fa-plus"></i>
                Create Task
              </button>
            )}
          </div>
        </div>

        <TaskStats stats={stats} />

        {/* Attendance Quick Section */}
        <div className="attendance-quick-section">
          <div className="section-header">
            <h2>
              <i className="fas fa-clock"></i>
              Today's Attendance
            </h2>
            <a href="/my-attendance" className="view-all-link">
              View Full Attendance →
            </a>
          </div>

          <div className="attendance-quick-cards">
            {/* Check-in Card */}
            <div className="attendance-quick-card">
              <div className="card-icon check-in">
                <i className="fas fa-sign-in-alt"></i>
              </div>
              <div className="card-content">
                <h3>Check In</h3>
                {attendanceData?.attendance?.checkIn?.time ? (
                  <div className="status-success">
                    <i className="fas fa-check-circle"></i>
                    <span>
                      Checked in at{" "}
                      {new Date(
                        attendanceData.attendance.checkIn.time
                      ).toLocaleTimeString("en-IN", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                        timeZone: "Asia/Kolkata"
                      })}
                    </span>
                  </div>
                ) : (
                  <div className="action-area">
                    {attendanceData?.canCheckIn ? (
                      <button
                        className="btn btn-check-in"
                        onClick={handleQuickCheckIn}
                        disabled={attendanceLoading}
                      >
                        {attendanceLoading ? "Checking In..." : "Check In Now"}
                      </button>
                    ) : (
                      <div className="restriction-info">
                        <i className="fas fa-clock"></i>
                        <span>10:00-11:00 AM</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Check-out Card */}
            <div className="attendance-quick-card">
              <div className="card-icon check-out">
                <i className="fas fa-sign-out-alt"></i>
              </div>
              <div className="card-content">
                <h3>Check Out</h3>
                {attendanceData?.attendance?.checkOut?.time ? (
                  <div className="status-success">
                    <i className="fas fa-check-circle"></i>
                    <span>
                      Checked out at{" "}
                      {new Date(
                        attendanceData.attendance.checkOut.time
                      ).toLocaleTimeString("en-IN", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                        timeZone: "Asia/Kolkata"
                      })}
                    </span>
                  </div>
                ) : (
                  <div className="action-area">
                    {attendanceData?.canCheckOut ? (
                      <button
                        className="btn btn-check-out"
                        onClick={handleQuickCheckOut}
                        disabled={attendanceLoading}
                      >
                        {attendanceLoading
                          ? "Checking Out..."
                          : "Check Out Now"}
                      </button>
                    ) : (
                      <div className="restriction-info">
                        <i className="fas fa-clock"></i>
                        <span>After 5:00 PM</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Status Card */}
            <div className="attendance-quick-card">
              <div className="card-icon status">
                <i className="fas fa-calendar-day"></i>
              </div>
              <div className="card-content">
                <h3>Today's Status</h3>
                <div className="attendance-status">
                  <span
                    className={`status-badge ${
                      attendanceData?.attendance?.status || "not-marked"
                    }`}
                  >
                    {attendanceData?.attendance?.status
                      ? attendanceData.attendance.status.toUpperCase()
                      : "NOT MARKED"}
                  </span>
                  {attendanceData?.attendance?.workingHours?.total > 0 && (
                    <div className="working-hours">
                      <i className="fas fa-hourglass-half"></i>
                      <span>
                        {Math.floor(
                          attendanceData.attendance.workingHours.total / 60
                        )}
                        h {attendanceData.attendance.workingHours.total % 60}m
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Task Update Status Cards with Monthly Report */}
        <div className="quick-update-status">
          <div
            className="update-status-item clickable"
            onClick={() => handleUpdateStatusClick("today")}
          >
            <i className="fas fa-clock"></i>
            <div className="status-info">
              <span className="status-number">
                {
                  tasks.filter((task) => task.updatesSummary?.hasTodaysUpdate)
                    .length
                }
              </span>
              <span className="status-label">Tasks Updated Today</span>
            </div>
            <i className="fas fa-chevron-right status-arrow"></i>
          </div>
          
          <div
            className="update-status-item clickable"
            onClick={() => handleUpdateStatusClick("all")}
          >
            <i className="fas fa-calendar-day"></i>
            <div className="status-info">
              <span className="status-number">
                {
                  tasks.filter(
                    (task) => task.updatesSummary?.totalDailyUpdates > 0
                  ).length
                }
              </span>
              <span className="status-label">Tasks with Updates</span>
            </div>
            <i className="fas fa-chevron-right status-arrow"></i>
          </div>
          
          {/* New Monthly Report Quick Access Card */}
          {canCreateTasks && (
            <div
              className="update-status-item clickable report-card"
              onClick={() => setShowMonthlyReport(true)}
            >
              <i className="fas fa-chart-line"></i>
              <div className="status-info">
                <span className="status-number">
                  {new Date().toLocaleDateString('en-US', { month: 'short' })}
                </span>
                <span className="status-label">Monthly Report</span>
              </div>
              <i className="fas fa-chevron-right status-arrow"></i>
            </div>
          )}
        </div>

        <div className="task-controls">
          <div className="search-container">
            <div className="search-box">
              <i className="fas fa-search"></i>
              <input
                type="text"
                placeholder="Search tasks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <TaskFilters filters={filters} onFilterChange={setFilters} />
        </div>

        <div className="task-grid">
          {filteredTasks.length === 0 ? (
            <div className="no-tasks">
              <i className="fas fa-tasks"></i>
              <h3>No tasks found</h3>
              <p>
                {searchTerm
                  ? "Try adjusting your search criteria"
                  : canCreateTasks
                  ? "Create your first task to get started"
                  : "No tasks assigned to you yet"}
              </p>
            </div>
          ) : (
            filteredTasks.map((task) => (
              <TaskCard
                key={task._id}
                task={task}
                onUpdate={handleTaskUpdate}
                onDelete={handleTaskDelete}
                userRole={userRole}
              />
            ))
          )}
        </div>

        {/* Create Task Modal */}
        {showCreateTask && (
          <CreateTask
            onClose={() => setShowCreateTask(false)}
            onTaskCreated={handleTaskCreated}
          />
        )}

        {/* Monthly Report Modal */}
        {showMonthlyReport && (
          <MonthlyTaskReport onClose={() => setShowMonthlyReport(false)} />
        )}

        {/* Update Details Modal */}
        {showUpdateModal && (
          <div
            className="modal-overlay"
            onClick={() => setShowUpdateModal(false)}
          >
            <div className="update-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>
                  {updateModalType === "today"
                    ? "Tasks Updated Today"
                    : "All Tasks with Updates"}
                </h2>
                <button
                  className="close-btn"
                  onClick={() => setShowUpdateModal(false)}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <div className="modal-content">
                {updateDetails.length === 0 ? (
                  <div className="no-data">
                    <i className="fas fa-calendar-times"></i>
                    <p>No updates found</p>
                  </div>
                ) : (
                  <div className="update-details-list">
                    {updateDetails.map((item, index) => (
                      <div key={index} className="update-detail-item">
                        <div className="task-info">
                          <h4 className="task-title">{item.title}</h4>
                          <div className="task-metadata">
                            <div className="assignee-info">
                              <i className="fas fa-user"></i>
                              <span>{item.assignedTo}</span>
                            </div>
                            <div className="task-badges">
                              <span
                                className={`priority-badge ${item.priority}`}
                              >
                                {item.priority}
                              </span>
                              <span className={`status-badge ${item.status}`}>
                                {item.status}
                              </span>
                            </div>
                          </div>
                        </div>

                        {updateModalType === "today" && item.update && (
                          <div className="update-content">
                            <div className="update-work">
                              <strong>Today's Work:</strong>
                              <p>{item.update.workDone}</p>
                            </div>
                            <div className="update-meta">
                              <span className="time-spent">
                                <i className="fas fa-clock"></i>
                                {Math.floor(item.timeSpent / 60)}h{" "}
                                {item.timeSpent % 60}m
                              </span>
                              <span className="update-time">
                                <i className="fas fa-calendar"></i>
                                {getTimeAgo(item.update.date)}
                              </span>
                            </div>
                            {item.update.challenges && (
                              <div className="challenges">
                                <strong>Challenges:</strong>
                                <p>{item.update.challenges}</p>
                              </div>
                            )}
                            {item.update.nextDayPlan && (
                              <div className="next-day-plan">
                                <strong>Next Day Plan:</strong>
                                <p>{item.update.nextDayPlan}</p>
                              </div>
                            )}
                          </div>
                        )}

                        {updateModalType === "all" && (
                          <div className="update-summary">
                            <div className="summary-stats">
                              <div className="stat">
                                <span className="stat-label">
                                  Total Updates:
                                </span>
                                <span className="stat-value">
                                  {item.totalUpdates}
                                </span>
                              </div>
                              <div className="stat">
                                <span className="stat-label">Total Time:</span>
                                <span className="stat-value">
                                  {item.totalTimeSpent}h
                                </span>
                              </div>
                            </div>
                            {item.lastUpdate && (
                              <div className="last-update">
                                <strong>Last Update:</strong>
                                <span>{getTimeAgo(item.lastUpdate)}</span>
                              </div>
                            )}
                            {item.latestWork && (
                              <div className="latest-work">
                                <strong>Latest Work:</strong>
                                <p>{item.latestWork.substring(0, 150)}...</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default TaskDashboard;